import React from 'react';
import { Container, Typography, Box, Paper, Button, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { SiteFooter } from '../components/SiteFooter';
import { BrandLogo } from '../components/BrandLogo';

/*
  A fixed date, changed by hand whenever the wording changes. It used to be `new Date()`, which
  claimed the policy had been updated on whatever day you happened to read it.
*/
const LAST_UPDATED = 'October 16, 2026';

export const Privacy: React.FC = () => {
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={0} sx={{ p: { xs: 2.5, sm: 4 } }}>
        {/* Matches the About page: logo, then the heading. */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <BrandLogo maxWidth={260} height={56} />
        </Box>

        <Typography variant="h3" component="h1" gutterBottom>
          Privacy Policy
        </Typography>
        <Typography variant="caption" color="text.secondary" gutterBottom display="block">
          Last Updated: {LAST_UPDATED}
        </Typography>

        {/*
          Every claim here should stay true of the running app — LinkedIn's API review compares
          this page against the access form. If a feature changes what data is collected or who
          it goes to, change this page in the same commit.
        */}
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
            1. Who We Are
          </Typography>
          <Typography paragraph>
            ShoutVector is a social media publishing tool built and operated by REMAGINE LLC ("we", "us"). It is an internal tool: we use it to publish content to the
            social media accounts and Company Pages that we and our affiliated companies own. There is no public sign-up;
            every account is created by us for our own staff.
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
            2. Information We Collect
          </Typography>
          <Typography component="ul" sx={{ pl: 4 }}>
            <li>
              <strong>Account information</strong> for the people we give access to: name, email address, and a password,
              which is stored only in hashed form.
            </li>
            <li>
              <strong>Content you create</strong> in ShoutVector: posts, captions, images, and scheduling choices.
            </li>
            <li>
              <strong>Connected social accounts</strong>: when an administrator connects a social media account, we store
              the access token the platform issues, the account's name and ID, and the list of Pages or Organizations that
              account administers, so they can be chosen as publishing targets.
            </li>
            <li>
              <strong>Results and statistics for our own accounts</strong>: the IDs and links of posts we publish, and
              figures the platforms report about our own Pages and posts, such as follower counts, impressions, clicks,
              likes, and comments.
            </li>
            <li>
              <strong>An activity log</strong> of actions taken in ShoutVector (for example, who published or deleted a
              post), kept so we can see what happened and fix problems.
            </li>
          </Typography>
          <Typography paragraph sx={{ mt: 2 }}>
            We do not use advertising or analytics trackers, and we do not collect information about the members of the
            public who view or interact with our posts beyond the totals the platforms report to us.
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
            3. How We Use Information
          </Typography>
          <Typography component="ul" sx={{ pl: 4 }}>
            <li>To publish, schedule, and remove posts on the accounts and Pages we own</li>
            <li>To show our staff how those posts and Pages are performing</li>
            <li>To keep the service secure, working, and backed up</li>
            <li>To send our staff account and service notices, such as password resets and publishing failures</li>
          </Typography>
          <Typography paragraph sx={{ mt: 2 }}>
            We do not use any of this information for advertising, marketing, or sales, and we do not sell it.
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
            4. LinkedIn Data
          </Typography>
          <Typography paragraph>
            When a Page administrator connects LinkedIn, ShoutVector uses LinkedIn's APIs only to: identify the
            administrator who connected (name and member ID); list the Company Pages that administrator manages; publish,
            and when asked, delete posts on those Pages; and read those Pages' own posts and statistics. We access no
            other member data, we do not combine LinkedIn data across organizations we do not administer, and we never
            share LinkedIn data with anyone else. Disconnecting LinkedIn erases its stored token at once, and we delete the
            rest of the LinkedIn data we hold, such as Page lists, post results, and statistics, on request from LinkedIn
            or the Page owner.
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
            5. Who We Share Information With
          </Typography>
          <Typography paragraph>
            We share information only with:
          </Typography>
          <Typography component="ul" sx={{ pl: 4 }}>
            <li>
              The social media platforms you connect (LinkedIn, Facebook, Instagram, and X), which receive the posts you
              choose to publish to them
            </li>
            <li>Amazon Web Services, which stores our backups</li>
            <li>Our email provider, which delivers account and service notices</li>
            <li>Authorities, where the law requires it</li>
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
            6. Security
          </Typography>
          <Typography paragraph>
            ShoutVector is served only over HTTPS from a server we control. Passwords are hashed, access tokens are kept
            on that server and used only to act on the accounts they were issued for, and access to the server is
            limited to our administrators.
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
            7. Retention and Deletion
          </Typography>
          <Typography paragraph>
            We keep information while it is needed to run the service. A deleted post can be restored for 30 days and is
            then removed permanently. Disconnecting a social account removes its stored token. Backups are kept for up to
            eight weeks, so deleted information is gone from backups within that time. To have your information deleted,
            contact us using the details below.
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
            8. Your Rights
          </Typography>
          <Typography paragraph>
            You can ask us to show you, correct, or delete the information we hold about you. You can also revoke
            ShoutVector's access to a social account at any time, either in ShoutVector or in that platform's own
            settings.
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
            9. Changes to This Policy
          </Typography>
          <Typography paragraph>
            If we change this policy, we will post the new version on this page and update the "Last Updated" date above.
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
            10. Contact Us
          </Typography>
          <Typography paragraph>
            If you have any questions about this Privacy Policy, please contact us at:
          </Typography>
          <Typography paragraph>
            <strong>REMAGINE LLC</strong><br />
            {/*
              A MUI Link rather than a bare <a> so it takes the theme's primary colour — the
              same colour the Back to Home button below uses. Matching by construction rather
              than by a copied hex, so a theme change moves both together.
            */}
            <Link
              href="https://www.remagine.com/contact"
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
            >
              Contact Support
            </Link>
          </Typography>
        </Box>

        {/*
          Points at "/" rather than "/login": signed out that redirects to the sign-in screen
          anyway, and signed in it lands on the Dashboard. Same as the About page.
        */}
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Button component={RouterLink} to="/">
            Back to Home
          </Button>
        </Box>
      </Paper>
      <SiteFooter variant="standalone" />
    </Container>
  );
};
